import React, { useState } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box, Container, Paper, Typography, TextField, FormControl, InputLabel,
  Select, MenuItem, RadioGroup, Radio, FormControlLabel, FormLabel,
  Chip, Button, Snackbar, Alert, Stack, Link
} from '@mui/material';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import { useAuth } from '../contexts/AuthContext';
import { submitFeatureRequest } from '../services/api';

const ROLES = ['Researcher', 'Journalist', 'Student', 'Government staff', 'Citizen', 'Developer', 'Other'];
const CATEGORIES = [
  'Search & Filtering', 'Data Display & Visualization', 'New Data Source',
  'Export & Download', 'User Account Features', 'API & Integration',
  'Accessibility', 'Performance', 'Mobile Experience', 'Other',
];
const DATA_TYPES = ['Hansard', 'Bills', 'Parliamentarians', 'Committees', 'Divisions', 'Questions', 'Petitions', 'Other'];
const IMPORTANCE = [
  { value: 'nice_to_have', label: 'Nice to have — would improve my experience' },
  { value: 'important', label: 'Important — I need this for my work' },
  { value: 'critical', label: 'Critical — blocking my use of ParlSearch' },
];

export default function FeatureRequestPage() {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [role, setRole] = useState('');
  const [roleOther, setRoleOther] = useState('');
  const [need, setNeed] = useState('');
  const [benefit, setBenefit] = useState('');
  const [category, setCategory] = useState('');
  const [dataTypes, setDataTypes] = useState([]);
  const [importance, setImportance] = useState('nice_to_have');
  const [context, setContext] = useState('');
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [submittedId, setSubmittedId] = useState(null);

  if (!authLoading && !isAuthenticated) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LightbulbOutlinedIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h5" gutterBottom>Submit a Feature Request</Typography>
          <Typography color="text.secondary" paragraph>
            Please sign in to submit a feature request so we can keep you
            updated on its progress.
          </Typography>
          <Button variant="contained" onClick={() => navigate('/login')}>
            Sign In
          </Button>
        </Paper>
      </Container>
    );
  }

  const roleInvalid = role === '';
  const needInvalid = need.trim() === '';
  const benefitInvalid = benefit.trim() === '';
  const categoryInvalid = category === '';
  const formInvalid = roleInvalid || needInvalid || benefitInvalid || categoryInvalid;

  const toggleDataType = (dt) => {
    setDataTypes((prev) =>
      prev.includes(dt) ? prev.filter((x) => x !== dt) : [...prev, dt]
    );
  };

  const handleSubmit = async () => {
    if (formInvalid) {
      setTouched(true);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const res = await submitFeatureRequest({
        user_role: role,
        user_role_other: role === 'Other' ? roleOther.trim() || null : null,
        need_description: need.trim(),
        benefit_description: benefit.trim(),
        category,
        related_data_types: dataTypes,
        importance,
        additional_context: context.trim() || null,
      });
      setSubmittedId(res.id);
    } catch (e) {
      setError('Could not submit your request. Please try again later.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submittedId) {
    return (
      <Container maxWidth="sm" sx={{ mt: 4 }}>
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <LightbulbOutlinedIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" gutterBottom>Thanks for your suggestion!</Typography>
          <Typography color="text.secondary" paragraph>
            Your feature request has been submitted (reference {submittedId}).
            We'll review it and you can track its status any time.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center">
            <Button variant="contained" component={RouterLink} to="/my-requests">
              View My Requests
            </Button>
            <Button onClick={() => { setSubmittedId(null); setRole(''); setNeed(''); setBenefit(''); setCategory(''); setDataTypes([]); setImportance('nice_to_have'); setContext(''); setTouched(false); }}>
              Submit Another
            </Button>
          </Stack>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, mb: 6 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
        <LightbulbOutlinedIcon color="primary" />
        <Typography variant="h4">Submit a Feature Request</Typography>
      </Box>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        Tell us what you'd like ParlSearch to do. We use the classic user-story
        format so we understand who needs it and why.
      </Typography>

      <Paper sx={{ p: { xs: 2, sm: 4 }, borderRadius: 3 }}>
        <Stack spacing={3}>
          <FormControl fullWidth error={touched && roleInvalid}>
            <InputLabel id="fr-role-label">As a…</InputLabel>
            <Select
              labelId="fr-role-label"
              label="As a…"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              {ROLES.map((r) => <MenuItem key={r} value={r}>{r}</MenuItem>)}
            </Select>
          </FormControl>

          {role === 'Other' && (
            <TextField
              label="Your role"
              fullWidth
              value={roleOther}
              onChange={(e) => setRoleOther(e.target.value)}
              inputProps={{ maxLength: 100 }}
            />
          )}

          <TextField
            label="I need…"
            required
            multiline
            minRows={2}
            fullWidth
            value={need}
            onChange={(e) => setNeed(e.target.value.slice(0, 500))}
            error={touched && needInvalid}
            helperText={`${need.length}/500 — describe what you need or want to do`}
            placeholder="Describe what you need or want to do"
          />

          <TextField
            label="So that…"
            required
            multiline
            minRows={2}
            fullWidth
            value={benefit}
            onChange={(e) => setBenefit(e.target.value.slice(0, 300))}
            error={touched && benefitInvalid}
            helperText={`${benefit.length}/300 — explain the benefit or outcome`}
            placeholder="Explain the benefit or outcome"
          />

          <FormControl fullWidth error={touched && categoryInvalid}>
            <InputLabel id="fr-cat-label">Category</InputLabel>
            <Select
              labelId="fr-cat-label"
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {CATEGORIES.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>

          <Box>
            <FormLabel sx={{ display: 'block', mb: 1 }}>
              Related data types (optional)
            </FormLabel>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
              {DATA_TYPES.map((dt) => (
                <Chip
                  key={dt}
                  label={dt}
                  clickable
                  color={dataTypes.includes(dt) ? 'primary' : 'default'}
                  variant={dataTypes.includes(dt) ? 'filled' : 'outlined'}
                  onClick={() => toggleDataType(dt)}
                />
              ))}
            </Box>
          </Box>

          <FormControl>
            <FormLabel id="fr-importance-label">How important is this to you?</FormLabel>
            <RadioGroup
              aria-labelledby="fr-importance-label"
              value={importance}
              onChange={(e) => setImportance(e.target.value)}
            >
              {IMPORTANCE.map((o) => (
                <FormControlLabel key={o.value} value={o.value} control={<Radio />} label={o.label} />
              ))}
            </RadioGroup>
          </FormControl>

          <TextField
            label="Additional details (optional)"
            multiline
            minRows={3}
            fullWidth
            value={context}
            onChange={(e) => setContext(e.target.value.slice(0, 1000))}
            helperText={`${context.length}/1000 — any extra context, examples or references`}
          />

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Link component={RouterLink} to="/my-requests" underline="hover">
              View my requests
            </Link>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={submitting}
            >
              {submitting ? 'Submitting…' : 'Submit Request'}
            </Button>
          </Box>
        </Stack>
      </Paper>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" role="alert" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}
